import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSidebar } from '../../context/SidebarContext';
import { useAuth } from '../../context/AuthContext';
import './ActivityBar.css';

const ActivityBar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // NER-3142: el dashboard /metrics (MetricsPage, NER-2785) es cross-org
  // Nerdo-internal (adopción por vertical, ARPU, cohorts) — NO métricas del
  // tenant. El backend ya lo gatea con 403 para no-Nerdo, pero el item quedaba
  // visible en el sidebar y confundía a los clientes. Ocultamos el item para
  // tenants normales; la ruta /metrics sigue montada (accesible por URL directa).
  // `user.attributes` es el loginId (email) que expone AuthContext.
  const userEmail = (user?.attributes || user?.username || '').toLowerCase();
  const isNerdoAdmin = userEmail.endsWith('@nerdo.solutions');
  const {
    isPinned,
    togglePinned,
    activeGroup,
    setActiveGroup,
    setHasMultipleItems,
    closeMobileMenu,
  } = useSidebar();
  const sidebarRef = useRef(null);
  const [submenuTop, setSubmenuTop] = useState(48);
  const closeTimerRef = useRef(null);
  const [clickedOpen, setClickedOpen] = useState(false);

  // Navigation groups with CTA actions (dynamic based on section)
  const navGroups = [
    {
      id: 'dashboard',
      icon: 'dashboard',
      label: t('nav.dashboard', 'Dashboard'),
      items: [{ path: '/', label: t('nav.dashboard', 'Dashboard'), icon: 'dashboard' }],
    },
    {
      id: 'agenda',
      icon: 'calendar_month',
      label: t('nav.agenda', 'Agenda'),
      items: [{ path: '/agenda', label: t('nav.agenda', 'Agenda'), icon: 'calendar_month' }],
    },
    {
      id: 'bookings',
      icon: 'date_range',
      label: t('nav.bookings', 'Turnos'),
      items: [{ path: '/bookings', label: t('nav.bookings', 'Turnos'), icon: 'date_range' }],
    },
    {
      id: 'business',
      icon: 'storefront',
      label: t('nav.business', 'Negocio'),
      items: [
        { path: '/professionals', label: t('nav.professionals', 'Profesionales'), icon: 'badge' },
        { path: '/services', label: t('nav.services', 'Servicios'), icon: 'medical_services' },
        {
          path: '/coverages',
          label: t('nav.coverages', 'Coberturas'),
          icon: 'verified_user',
        },
      ],
    },
    {
      id: 'reports',
      icon: 'insights',
      label: t('nav.reports', 'Reportes'),
      items: [
        // NER-3145: métricas del propio negocio (dueño-facing). Visible para
        // todo el admin panel (sólo OWNER/ADMIN acceden); la ruta /analytics
        // además está gateada por ProtectedRoute allowedRoles OWNER/ADMIN.
        {
          path: '/analytics',
          label: t('nav.analytics', 'Analíticas'),
          icon: 'analytics',
        },
        {
          path: '/coverage-matrix',
          label: t('nav.coverageMatrix', 'Matriz de coberturas'),
          icon: 'grid_on',
        },
        // NER-3142: sólo visible para cuentas Nerdo admin (email @nerdo.solutions).
        ...(isNerdoAdmin
          ? [
              {
                path: '/metrics',
                label: t('nav.metrics', 'Métricas'),
                icon: 'insights',
              },
            ]
          : []),
      ],
    },
    {
      id: 'customers',
      icon: 'people',
      label: t('nav.customers', 'Clientes'),
      items: [
        { path: '/customers', label: t('nav.customers', 'Clientes'), icon: 'people' },
        {
          path: '/conversations',
          label: t('nav.conversations', 'Conversaciones'),
          icon: 'chat',
        },
      ],
    },
    {
      id: 'config',
      icon: 'settings',
      label: t('nav.config', 'Configuración'),
      // NER-1741: Usuarios y Suscripción quedan fuera del MVP Citame (sus
      // endpoints son del admin-panel Resto y no existen en el backend Citame,
      // producían "Error al cargar..."). Se ocultan del nav hasta que se definan
      // (Suscripciones: epic NER-1379, post-MVP).
      items: [{ path: '/settings', label: t('nav.settings', 'Ajustes'), icon: 'settings' }],
    },
    // NER-3256: Setup Wizard onboarding — sólo visible para cuentas Nerdo admin
    // (email @nerdo.solutions). La ruta /admin/setup-wizard además se auto-gatea.
    ...(isNerdoAdmin
      ? [
          {
            id: 'setup',
            icon: 'rocket_launch',
            label: t('nav.setupWizard', 'Setup Wizard'),
            items: [
              {
                path: '/admin/setup-wizard',
                label: t('nav.setupWizard', 'Setup Wizard'),
                icon: 'rocket_launch',
              },
            ],
          },
        ]
      : []),
  ];

  // Get current active group based on path
  const getCurrentGroup = useCallback(() => {
    for (const group of navGroups) {
      for (const item of group.items) {
        if (location.pathname === item.path || location.pathname.startsWith(item.path + '/')) {
          return group.id;
        }
      }
    }
    return 'dashboard';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const currentActiveGroup = getCurrentGroup();

  // Close sidebar when clicking outside (only in floating mode)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isPinned && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        // Check if click is on activity bar icon
        const isActivityBarClick = event.target.closest('.activity-bar-item');
        if (!isActivityBarClick) {
          setActiveGroup(null);
          setHasMultipleItems(false);
          setClickedOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPinned, setActiveGroup, setHasMultipleItems]);

  // Handle icon hover (when sidebar is floating/not pinned)
  const handleGroupHover = (group, event) => {
    // Only show submenu on hover if sidebar is not pinned (floating mode)
    if (!isPinned && group.items.length > 1) {
      // Calculate the position of the button
      const buttonRect = event.currentTarget.getBoundingClientRect();
      let top = buttonRect.top;

      // Estimate submenu height (roughly 48px per item + header)
      const estimatedHeight = group.items.length * 48 + 48;
      const maxTop = window.innerHeight - estimatedHeight - 16;

      // Adjust if it would go off screen
      if (top + estimatedHeight > window.innerHeight - 16) {
        top = Math.max(8, maxTop);
      }

      setSubmenuTop(top);
      setActiveGroup(group.id);
      setHasMultipleItems(true);
    }
  };

  // Handle mouse leave from activity bar area
  const handleActivityBarLeave = () => {
    // Close submenu after a small delay when mouse leaves if not pinned
    // But keep it open if it was explicitly clicked open
    if (!isPinned && !clickedOpen) {
      closeTimerRef.current = setTimeout(() => {
        setActiveGroup(null);
        setHasMultipleItems(false);
      }, 300); // 300ms delay
    }
  };

  // Handle mouse enter to activity bar - cancel close timer
  const handleActivityBarEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  // Handle mouse enter to submenu - cancel close timer
  const handleSubmenuEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  // Handle mouse leave from submenu
  const handleSubmenuLeave = () => {
    if (!isPinned && !clickedOpen) {
      closeTimerRef.current = setTimeout(() => {
        setActiveGroup(null);
        setHasMultipleItems(false);
      }, 300); // 300ms delay
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  // Handle icon click
  const handleGroupClick = (group) => {
    // If single item group, navigate directly without showing submenu
    if (group.items.length === 1) {
      navigate(group.items[0].path);
      closeMobileMenu();
      setActiveGroup(null);
      setHasMultipleItems(false);
      setClickedOpen(false);
      return;
    }

    // Toggle submenu — if already open via click, close it
    if (activeGroup === group.id && clickedOpen) {
      setActiveGroup(null);
      setHasMultipleItems(false);
      setClickedOpen(false);
      return;
    }

    // Open submenu persistently (won't close on mouse leave)
    setActiveGroup(group.id);
    setHasMultipleItems(true);
    setClickedOpen(true);
    if (activeGroup !== group.id) {
      navigate(group.items[0].path);
    }
  };

  // Handle submenu item click — keep submenu open so user can browse items
  const handleItemClick = (path) => {
    navigate(path);
    closeMobileMenu();
  };

  // Close secondary sidebar
  const closeSidebar = () => {
    if (!isPinned) {
      setActiveGroup(null);
      setHasMultipleItems(false);
      setClickedOpen(false);
    }
  };

  // Get the current group data
  const activeGroupData = navGroups.find((g) => g.id === activeGroup);
  const showSecondarySidebar = activeGroup && activeGroupData && activeGroupData.items.length > 1;

  return (
    <>
      {/* Mobile backdrop for activity bar drawer */}
      <div className="mobile-menu-backdrop" onClick={closeMobileMenu} />
      <div
        className="activity-bar"
        onMouseLeave={handleActivityBarLeave}
        onMouseEnter={handleActivityBarEnter}
      >
        {/* Menu Toggle Button - Pin/Unpin sidebar */}
        <div className="activity-bar-logo">
          <button
            type="button"
            className={`activity-bar-menu-btn ${isPinned ? 'pinned' : ''}`}
            onClick={togglePinned}
            title={isPinned ? t('common.unpin', 'Despinear menú') : t('common.pin', 'Pinear menú')}
            aria-label={
              isPinned ? t('common.unpin', 'Despinear menú') : t('common.pin', 'Pinear menú')
            }
          >
            <span className="material-icons">menu</span>
          </button>
        </div>

        {/* Navigation Icons */}
        <div className="activity-bar-icons">
          {navGroups.map((group) => (
            <div key={group.id} className="activity-bar-item-wrapper">
              <button
                type="button"
                className={`activity-bar-item ${currentActiveGroup === group.id ? 'active' : ''} ${activeGroup === group.id ? 'flyout-open' : ''}`}
                onClick={() => handleGroupClick(group)}
                onMouseEnter={(e) => handleGroupHover(group, e)}
                title={group.label}
                aria-label={group.label}
              >
                <span className="material-icons">{group.icon}</span>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Backdrop for floating mode */}
      {showSecondarySidebar && !isPinned && (
        <div className="sidebar-backdrop" onClick={closeSidebar} />
      )}

      {/* Secondary Sidebar */}
      {showSecondarySidebar && (
        <div
          ref={sidebarRef}
          className={`secondary-sidebar ${isPinned ? 'pinned' : 'floating'}`}
          style={!isPinned ? { top: `${submenuTop}px` } : {}}
          onMouseEnter={handleSubmenuEnter}
          onMouseLeave={handleSubmenuLeave}
        >
          <div className="secondary-sidebar-header">
            <span className="secondary-sidebar-title">{activeGroupData.label}</span>
            <div className="secondary-sidebar-actions">
              {/* Close Button (only in floating mode) */}
              {!isPinned && (
                <button
                  type="button"
                  className="sidebar-action-btn"
                  onClick={closeSidebar}
                  title={t('common.close', 'Cerrar')}
                  aria-label={t('common.close', 'Cerrar')}
                >
                  <span className="material-icons">close</span>
                </button>
              )}
            </div>
          </div>

          <div className="secondary-sidebar-items">
            {activeGroupData.items.map((item) => (
              <button
                type="button"
                key={item.path}
                className={`secondary-sidebar-item ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => handleItemClick(item.path)}
              >
                <span className="material-icons">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          {/* CTA Button - Dynamic based on section - In container */}
          {activeGroupData.cta && (
            <div className="secondary-sidebar-footer">
              <button
                type="button"
                className="sidebar-cta-btn-full"
                onClick={activeGroupData.cta.action}
                title={activeGroupData.cta.label}
              >
                <span className="material-icons">{activeGroupData.cta.icon}</span>
                <span>{activeGroupData.cta.label}</span>
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ActivityBar;
